import every from 'lodash/every';
import flatten from 'lodash/flatten';
import first from 'lodash/fp/first';
import flow from 'lodash/fp/flow';
import map from 'lodash/fp/map';
import mapValues from 'lodash/fp/mapValues';
import orderBy from 'lodash/fp/orderBy';
import pickBy from 'lodash/fp/pickBy';
import values from 'lodash/fp/values';
import some from 'lodash/some';

import {DEFAULT_PARENT_ID} from '../../../../components/Organizations/constants';
import {reduceTree} from '../../organization/utils';

export function getFirstOrgIdSorted(orgs) {
  return flow([orderBy([(org) => org.name.toLowerCase().trim()], ['asc']), map('id'), first])(orgs);
}

export function getDefaultOrgId(state, {requirePermissions = [], anyOfPermissions = [], noRoot = false}) {
  const fullPermissions = state?.session?.fullPermissions;
  const organizationHierarchy = state?.session?.organizationHierarchy;
  const orgs = getOrganizationHierarchyWithAccess(organizationHierarchy, fullPermissions, {
    requirePermissions,
    anyOfPermissions,
    noRoot,
  });

  return getFirstOrgIdSorted(orgs);
}

export function getOrganizationHierarchyWithAccess(
  organizationHierarchy,
  fullPermissions,
  {requirePermissions, anyOfPermissions, noRoot, organizationLimit},
) {
  // from permissions what organization roles grant the required permissions
  const organizationsWithPermissions = getOrgsWithPermissions(fullPermissions, {requirePermissions, anyOfPermissions});

  // create a prunes hierarchy limited to organizations with required permissions
  const orgs = findTopLevelOrgsWithPermissions(
    organizationHierarchy?.[0],
    organizationsWithPermissions,
    organizationLimit,
  );

  return noRoot && orgs?.[0]?.id === DEFAULT_PARENT_ID ? orgs[0].children : orgs;
}

export function getOrgsWithPermissions(fullPermissions, {requirePermissions, anyOfPermissions}) {
  return flow([
    pickBy((accessRole) => {
      const hasRequiredPermissionsAccess =
        requirePermissions?.length > 0
          ? every(requirePermissions, (permission) => accessRole?.permissions[permission])
          : true;
      const hasAnyOfPermissionsAccess =
        anyOfPermissions?.length > 0
          ? some(anyOfPermissions, (permission) => accessRole?.permissions[permission])
          : true;
      return hasRequiredPermissionsAccess && hasAnyOfPermissionsAccess;
    }),
    mapValues('orgId'),
    values,
  ])(fullPermissions);
}

/**
 * Loop through organization (hierarchy) traversing the children to find the highest level organizations
 * that has the correct access. This finding is limited to the organizationLimit list, which normally
 * only affects users with complex access rights.
 *
 * End result will be list of highest level organizations within organizationLimit which has the permission
 * access
 *
 * @param organization
 * @param organizationsWithPermissions
 * @param organizationLimit
 * @param permissionOverride - should be initially false unless internally turned on
 * @returns {unknown[]|*[]}
 */
export function findTopLevelOrgsWithPermissions(
  organization,
  organizationsWithPermissions,
  organizationLimit,
  permissionOverride = false,
) {
  // if (no limit OR org is within limit) AND (organization or parent(permissionOverride) has permission OR root has permission)
  if (
    (!organizationLimit || organizationLimit?.includes(organization?.id)) &&
    (permissionOverride ||
      organizationsWithPermissions.includes(organization?.id) ||
      organizationsWithPermissions?.includes(DEFAULT_PARENT_ID))
  ) {
    return [organization];
  } else {
    return flatten(
      organization?.children?.map((org) =>
        findTopLevelOrgsWithPermissions(
          org,
          organizationsWithPermissions,
          organizationLimit,
          organizationsWithPermissions.includes(organization?.id),
        ),
      ),
    );
  }
}

export function filterOrgsPerAvailablePersonas(state, currentLocaleStorageOrgs) {
  const fullPermissions = state?.session?.fullPermissions;
  const hasRoot = fullPermissions?.[DEFAULT_PARENT_ID];
  if (hasRoot) {
    return currentLocaleStorageOrgs;
  }

  const organizationHierarchy = state?.session?.organizationHierarchy;
  const currentAvailablePersonas = reduceTree(organizationHierarchy)?.map((org) => org.id);
  return currentLocaleStorageOrgs?.filter((orgId) => {
    return currentAvailablePersonas?.includes(orgId);
  });
}

// transform all aggregated permission ids array to an object of ids
export function getAggregatedPermissionIdsObject(state) {
  return state?.session?.aggregatedPermissionIds?.reduce((res, item) => ({...res, [item]: item}), {});
}
